<template>
  <div>
    <exo-form hideClose :root="_self" :clear="false">
      <template v-slot:titleTooltip>
        <exo-help-dialog
          :color="'black--text'"
          margin="m-0"
          title="Simulator Mission"
        >
          <b>General description</b>
          <br />TODO
        </exo-help-dialog>
      </template>
      <exo-input-name :root="_self" />

      <v-row>
        <v-col cols="6">
          <exo-card-form label="Initial conditions">
            <exo-input-ref-notparametric
              :root="_self"
              name="spacecraft"
              type="spacecraft"
              componentRoute="/numericalspacecraft"
            />
            <v-row>
              <exo-form-alert
                type="info"
                class="ml-4 mr-2"
                v-if="this.inputs.spacecraft"
              >
                Spacecraft propulsion system is considered
                {{ isConstantOrImpulsive }}
              </exo-form-alert>
            </v-row>

            <exo-input-ref-notparametric
              :root="_self"
              name="initialOrbit"
              type="orbit"
              componentRoute="/numericalorbit"
            />

            <v-row v-if="this.inputs.initialOrbit != null">
              <v-col> Orbit Date : {{ getDateTimeString() }} </v-col>
            </v-row>
            <exo-input-checkbox
              :disabled="true"
              v-if="this.inputs.initialOrbit != null"
              :root="_self"
              name="overloadOrbitDateTime"
            >
              <exo-help-dialog :title="getLabel('overloadOrbitDateTime')">
                <b>Warning</b>
                <br />
                The simulation date is set to the orbit date by default. When
                changing the mission start date, the orbit date is implicitly
                adjusted to the new one. No propagation of the orbital state to
                the new date is performed. In other words, things go as if the
                orbit had been originally defined at the new start date and the
                mission start date had not been changed.<br />
              </exo-help-dialog>
            </exo-input-checkbox>
            <exo-input-date-time
              v-if="this.inputs.overloadOrbitDateTime"
              :root="_self"
              name="missionDateTime"
            />
          </exo-card-form>
          <exo-card-form label="Mission">
            <exo-input-select :root="_self" name="targetDateDefinitionType" />
            <exo-input-double :root="_self" name="duration" />
            <exo-input-date-time :root="_self" name="targetDate" />
            <v-btn
              small
              :disabled="
                !this.inputs.initialOrbit ||
                this.inputs.manoeuvreRoadmap != null
              "
              v-on:click="getDefaultRoadmap()"
              color="primary"
              dark
              >Generate Roadmap</v-btn
            >
            <v-row v-if="this.inputs.manoeuvreRoadmap"
              >Start date: {{ this.inputs.manoeuvreRoadmap.startDate }}
            </v-row>
            <v-row v-if="this.inputs.manoeuvreRoadmap"
              >End date: {{ this.inputs.manoeuvreRoadmap.stopDate }}
            </v-row>
            <exo-input-double
              :root="_self"
              name="smaTolerance"
              tooltip="Semi-major axis deviation allowed with respect to the initial orbit"
            />
          </exo-card-form>
        </v-col>
        <v-col cols="6">
          <exo-card-form label="Simulation configuration">
            <exo-input-double :root="_self" name="maximumDuration">
              <exo-help-dialog title="Maximum simulation duration">
                This is the maximum duration expected for the mission. If the
                target is not reached after that duration, the mission would
                fail. This parameter is used to avoid unpredicted long
                simulation.
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-select
              :multiple="true"
              :root="_self"
              name="perturbations"
              :disabledItemConfiguration="disabledPerturbationsConfiguration"
            >
              <exo-help-dialog title="Perturbations">
                <i
                  >Perturbations are not available in
                  <b>Maneuvering Strategy Design</b> missions.</i
                ><br />
                <i>Perturbations are limited in <b>RAAN Phasing</b> missions.</i
                ><br />
                <br />
                <br />
                <b>Earth potential</b>
                <br />
                The EGM96 model is used. The default values for the degree
                <katex-element expression="n" /> and order
                <katex-element expression="m" /> of the model depend on the
                orbit's semi-major axis <katex-element expression="a" /> as
                reported below:
                <br />
                <br />
                <katex-element
                  expression="(n, m) = (8, 8) \text{  for  } a \leq 13000 \text{ km}"
                />;<br />
                <katex-element
                  expression="(n, m) = (6, 6) \text{  for  } 13000 \text{ km} < a < 26000 \text{ km}"
                />;<br />
                <katex-element
                  expression="(n, m) = (4, 4) \text{  for  }a \geq 26000 \text{ km}"
                />.<br />
                <br />
                <br />
                These values can be changed by the user by using a custom
                configuration.<br />
                <br />
                <b>Third body</b>: the perturbative effect of Sun and Moon can
                be considered. JPL ephemerides are used to compute bodies'
                positions and velocities at a given date.
                <br />
                Note: As of now, when this perturbation is taken into account,
                the end date of the mission must be before 2068 (because the
                ephemerides of the two perturbing bodies have been limited to
                this date).
                <br />
                <br />
                <b>Atmospheric drag</b>
                <br />
                Atmospheric drag heavily affects the spacecraft’s motion in LEO.
                The acceleration due to drag is given by:
                <br />
                <br />
                <div class="text-xs-center">
                  <katex-element
                    expression="\mathbf{a}_{drag}=-0.5 (C_D\frac{A}{m})\rho v_{rel}\mathbf{v_{rel}}"
                  />
                </div>
                <br />

                Where:
                <br />
                <katex-element expression="\mathbf{v_{rel}}" />
                : the relative velocity of the spacecraft with respect to the
                atmosphere.
                <br />
                <katex-element expression="C_D" />
                : the drag coefficient, which takes into account the complex
                interactions between the flow and the spacecraft. It is an
                empirical measure of the body's susceptibility to drag forces.
                <br />
                For satellites in the upper atmosphere is often of the order of
                2.2 (flat plate approximation).
                <br />
                <br />
                The model consider for simulating the Earth's atmosphere is the
                Harris-Priester model, which relies on tabulated data in order
                to estimate the value of the atmospheric density. According to
                the selected value for the solar flux, the tables can be
                interpolated to return a more accurate density estimate.
                <br />
                <br />
                <b>Solar radiation pressure</b>
                <br />
                The incident flux of solar particles from the Sun, is absorbed
                and then reflected by the spacecraft's surface. This interaction
                will produce a force on the spacecraft. Formula are not included
                here, but the interested reader can consult the reference (see
                below).
                <br />
                The radiative properties of the spacecraft are defined by two
                coefficients:
                <br />
                <ul>
                  <li>
                    Coefficient: this coefficient which must be between 0 and 1
                  </li>
                  <li>
                    Reflection Coefficient: this is the (specular) reflection
                    coefficient and must be between 0 and 1.
                  </li>
                </ul>
                <br />
                <b>Unperturbed motion</b>
                When no perturbation forces are selected, the propagation is
                equivalent to an unperturbed Keplerian motion around a point
                mass (having the same gravitational constant than the Earth). In
                this case, the orbital elements do not change except for the
                anomaly (angular position on the orbit).
                <br />
                <br />
                <br />
                <b>References</b>
                <br />
                D.A. Vallado, Fundamentals of Astrodynamics and Applications,
                4th ed, 2014, Space Technology Library.
              </exo-help-dialog>
            </exo-input-select>
            <v-row>
              <exo-form-alert
                type="warning"
                class="ml-4 mr-2"
                v-if="perturbationsUnselected"
              >
                Warning: If all perturbations are not selected, this will lead
                to a generation of data that no longer simulates real behavior
              </exo-form-alert>
            </v-row>

            <exo-input-double
              :root="_self"
              v-if="
                inputs.perturbations != null &&
                inputs.perturbations.includes('DRAG')
              "
              name="customSolarFlux"
            >
              <exo-help-dialog title="Solar flux">
                The value of the solar flux
                <katex-element expression="F_{10.7}" /> is correlated to the
                atmospheric density. As a rule of thumb, increasing the solar
                flux increases the density. For instance:
                <br />
                <ul>
                  <li>
                    solar flux 65 corresponds to low solar activity and minimum
                    density.
                  </li>
                  <li>solar flux 150 is an average value.</li>
                  <li>
                    solar flux 275 corresponds to strong solar activity and
                    maximum density.
                  </li>
                </ul>
              </exo-help-dialog>
            </exo-input-double>
            <v-row
              no-gutters
              class="ml-0"
              v-if="
                isRequiredOrOptional('earthPotentialConfiguration', inputs) &&
                inputs.perturbations != null &&
                inputs.perturbations.includes('EARTH_POTENTIAL')
              "
            >
              <v-col cols="4">
                <exo-input-select
                  :root="_self"
                  name="earthPotentialConfiguration"
                >
                  <exo-help-dialog title="Earth potential">
                    The EGM96 model is used. The default values for the degree
                    <katex-element expression="n" /> and order
                    <katex-element expression="m" /> of the model depend on the
                    orbit's semi-major axis <katex-element expression="a" /> as
                    reported below:
                    <br />
                    <br />
                    <katex-element
                      expression="(n, m) = (8, 8) \text{  for  } a \leq 13000 \text{ km}"
                    />;<br />
                    <katex-element
                      expression="(n, m) = (6, 6) \text{  for  } 13000 \text{ km} < a < 26000 \text{ km}"
                    />;<br />
                    <katex-element
                      expression="(n, m) = (4, 4) \text{  for  }a \geq 26000 \text{ km}"
                    />.<br />
                    <br />
                    <br />
                    These values can be changed by the user by using a custom
                    configuration.<br />
                  </exo-help-dialog>
                </exo-input-select>
              </v-col>
              <v-col cols="3" class="mr-4 ml-6">
                <exo-input-double
                  :root="_self"
                  name="customEarthPotentialDegree"
                />
              </v-col>
              <v-col cols="3">
                <exo-input-double
                  :root="_self"
                  name="customEarthPotentialOrder"
                />
              </v-col>
            </v-row>
            <exo-form-alert type="info" v-if="isOAPMission">
              In this mission the earth potential pertubation is used to take
              the J2 effect into account.
            </exo-form-alert>
          </exo-card-form>
          <exo-card-form label="Measurement">
            <measurementGenerationRequest
              :measureGenerationRequestFunction="setMeasureGenerationRequest"
            >
            </measurementGenerationRequest>
          </exo-card-form>
        </v-col>
      </v-row>
    </exo-form>
    <v-col ref="NumericalMissionErrorDisplay"> </v-col>
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import numericalMixin from '../../mixins/numericalForms.js'
import plotLayout from '../../mixins/plotLayout.js'
import dataTools from '../../tools/data.js'
import missionTools from '../../store/mission.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import { mapGetters } from 'vuex'
import Vue from 'vue'
import thrustPattern from '@/components/Forms/ThrustPattern.vue'
import measurementGenerationRequest from '../Defense/MeasurementGenerationRequest.vue'
import mission from '../../store/mission.js'

const context = 'SimulationResult'

export default {
  name: 'numericalsimulatormission',

  mixins: [formMixin, numericalMixin, plotLayout],
  components: {
    thrustPattern,
    measurementGenerationRequest
  },
  computed: {
    ...mapGetters(['missions', 'currentMission']),
    eclipseOptionDisabled() {
      return (
        this.isOAPMission ||
        !this.isNumericalPropagation ||
        this.inputs.attitudeAndSystemSimulated === true ||
        this.inputs.stopThrustInEclipse === true
      )
    },

    isConstantOrImpulsive() {
      if (this.inputs.spacecraft.thruster.isp <= 500) {
        return 'impulsive thrust'
      } else {
        return 'constant thrust'
      }
    },
    currentStrategyChoice() {
      return this.inputs.maneuveringStrategyChoice
    },
    maxOrbitalDutyCycle() {
      const t = parseFloat(this.inputs.numberOfThrustOrbits)
      const r = parseFloat(this.inputs.numberOfRestOrbits)

      const maxMean = (this.maxMeanDutyCycle * (t + r)) / t
      const defaultValue = 100
      if (isNaN(maxMean)) {
        return defaultValue
      }
      return Math.min(defaultValue, maxMean)
    },
    maxMeanDutyCycle() {
      if (this.inputs.spacecraft && this.inputs.spacecraft.thruster) {
        const maxDutyCycle = dataTools.computeMaxDutyCycleWithWarmup(
          this.inputs.spacecraft.onBoardAveragePower,
          this.inputs.spacecraft.thruster.power,
          this.inputs.spacecraft.thruster.standbyPower,
          this.inputs.spacecraft.thruster.warmUpPower,
          this.warmupDutyCycle
        )
        if (maxDutyCycle !== '') {
          return maxDutyCycle
        }
      }
      return 100
    },
    warmupDutyCycle() {
      if (!this.inputs.initialOrbit) {
        return 0
      }
      const earthMU = 3.986004415e14
      const initialOrbitPeriod =
        2 *
        Math.PI *
        this.inputs.initialOrbit.sma *
        Math.sqrt(this.inputs.initialOrbit.sma / earthMU)
      const cycleDuration =
        (this.inputs.numberOfRestOrbits + this.inputs.numberOfThrustOrbits) *
        initialOrbitPeriod
      let thrustArcsNumberInt = 1
      if (this.inputs.thrustArcsNumber.value === 'ONE') {
        thrustArcsNumberInt = 1
      } else {
        thrustArcsNumberInt = 2
      }
      const arcsPerCycle =
        this.inputs.numberOfThrustOrbits * thrustArcsNumberInt
      return (
        (arcsPerCycle * this.inputs.spacecraft.thruster.warmUpDuration) /
        cycleDuration
      )
    },

    unconsistantThrustPattern() {
      let t = 1
      let r = 0
      let s = 0
      if (
        this.inputs.numberOfThrustOrbits != null &&
        !isNaN(this.inputs.numberOfThrustOrbits)
      ) {
        t = parseFloat(this.inputs.numberOfThrustOrbits)
      }
      if (
        this.inputs.numberOfRestOrbits != null &&
        !isNaN(this.inputs.numberOfRestOrbits)
      ) {
        r = parseFloat(this.inputs.numberOfRestOrbits)
      }
      if (
        this.inputs.numberOfShiftOrbits != null &&
        !isNaN(this.inputs.numberOfShiftOrbits)
      ) {
        s = parseFloat(this.inputs.numberOfShiftOrbits)
      }
      return s >= t + r
    },

    strategyId() {
      // depending on the mission
      if (this.inputs.type === 'Orbital Transfer') {
        let changeSemiMajorAxis = false
        let changeEccentricity = false
        let changeInclination = false
        changeSemiMajorAxis =
          this.inputs.deltaSemiMajorAxis != null &&
          !isNaN(this.inputs.deltaSemiMajorAxis) &&
          parseFloat(this.inputs.deltaSemiMajorAxis) !== 0
        changeEccentricity =
          this.inputs.deltaEccentricity != null &&
          !isNaN(this.inputs.deltaEccentricity) &&
          parseFloat(this.inputs.deltaEccentricity) !== 0
        changeInclination =
          this.inputs.deltaInclination != null &&
          !isNaN(this.inputs.deltaInclination) &&
          parseFloat(this.inputs.deltaInclination) !== 0
        return (
          'a=' +
          changeSemiMajorAxis +
          ',e=' +
          changeEccentricity +
          ',i=' +
          changeInclination
        )
      }
      return this.inputs.type
    },

    thrustArcsNumberItems() {
      let currentAvailableStrategies =
        this.maneuveringStrategies[this.strategyId]
      let enumItems = this.getPropertiesDescription()['thrustArcsNumber'].values
      if (currentAvailableStrategies != null) {
        enumItems = enumItems.filter((value) =>
          Object.keys(currentAvailableStrategies).includes(value.value)
        )
      }
      return enumItems
    },

    thrustArcsPositionItems() {
      let currentAvailableStrategies =
        this.maneuveringStrategies[this.strategyId]
      let enumItems =
        this.getPropertiesDescription()['thrustArcsPosition'].values
      if (
        currentAvailableStrategies != null &&
        currentAvailableStrategies[this.inputs.thrustArcsNumber] != null
      ) {
        enumItems = enumItems.filter((value) =>
          currentAvailableStrategies[this.inputs.thrustArcsNumber].includes(
            value.value
          )
        )
      }
      return enumItems
    },

    thrustAttitudeModeItems() {
      return this.getPropertiesDescription()['thrustAttitudeMode'].values
    },

    restAttitudeModeItems() {
      return this.getPropertiesDescription()['restAttitudeMode'].values
    },

    thrustArcsPositionItemsNotCustom() {
      return this.thrustArcsPositionItems.filter(
        (value) => value.value !== 'CUSTOM'
      )
    },

    disabledPerturbationsConfiguration() {
      if (this.inputs.type === 'RAAN Phasing') {
        return 'disabledOnRAAN'
      }
      if (this.inputs.type === 'LEO Station Keeping') {
        return 'disabledOnSKLEO'
      }
      return '' // enable all
    },

    isOAPMission() {
      return this.inputs.type === 'Maneuvering Strategy Design'
    },

    displayErrorEccentricityWithPerturbations() {
      return this.isPerturbationSelected && this.inputs.deltaEccentricity > 0
    },

    displayErrorEccentricityRAAN() {
      if (this.inputs.type === 'RAAN Phasing') {
        return (
          this.inputs.initialOrbit != null &&
          this.inputs.initialOrbit.eccentricity > 0.01
        )
      } else {
        return false
      }
    },

    displayErrorCoastingAltitude() {
      // Without Number(), the inputs would be compared as string (888>7777)
      return (
        Number(this.inputs.minCoastingAltitude) >
        Number(this.inputs.maxCoastingAltitude)
      )
    },

    displayErrorCoastingInclination() {
      // Without Number(), the inputs would be compared as string (888>7777)
      return (
        Number(this.inputs.minCoastingInclination) >
        Number(this.inputs.maxCoastingInclination)
      )
    },

    isPerturbationSelected() {
      return (
        this.inputs.perturbations != null &&
        this.inputs.perturbations.length > 0
      )
    },

    perturbationsUnselected() {
      return this.inputs.perturbations.length !== 4
    },

    isEphemeridesSelected() {
      return (
        this.inputs.ephemeridesRequested &&
        this.inputs.ephemeridesRequested.length > 0
      )
    },

    isEphemeridesFileTypesSelected() {
      return (
        this.inputs.ephemeridesFileTypesRequested &&
        this.inputs.ephemeridesFileTypesRequested.length > 0
      )
    },

    isEphemeridesStepRequested() {
      // for semi analytical, the step is automatic
      return (
        this.isNumericalPropagation &&
        (this.isEphemeridesSelected || this.isEphemeridesFileTypesSelected)
      )
    },

    expectedMaxDuration() {
      // for orbit extrapolation we could consider the case when target date provided
      let duration = 0
      for (let durationField of [
        'maximumDuration',
        'duration',
        'targetMissionDuration',
        'targetMissionDurationForAol'
      ]) {
        if (this.isRequiredOrOptional(durationField, this.inputs)) {
          duration = this.inputs[durationField]
        }
      }
      return duration
    },

    ephemeridesCount() {
      if (
        this.isEphemeridesStepRequested &&
        this.inputs.ephemeridesStepInSeconds > 0
      ) {
        let multiplyFactor =
          this.inputs.ephemeridesOscChoice && this.inputs.ephemeridesMeanChoice
            ? 2
            : 1

        return (
          multiplyFactor *
          Math.ceil(
            dataTools.convertGui2si(
              this.getFieldType('maximumDuration'),
              this.expectedMaxDuration
            ) / this.inputs.ephemeridesStepInSeconds
          )
        )
      } else {
        return 0
      }
    },

    ephemeridesData() {
      return (
        ((this.ephemeridesCount * this.ephemerisDataPercentage) / 3000) *
        100
      ).toFixed(2)
    },

    ephemerisDataPercentage() {
      return (
        this.ephemeridesNumberOfFieldsPerEphemeris /
        this.ephemeridesMaxNumberOfFieldsPerEphemeris
      ).toFixed(2)
    },

    ephemeridesCountFeedbackStyle() {
      return this.ephemeridesData < 80
        ? 'success'
        : this.ephemeridesData < 100
        ? 'warning'
        : 'error'
    },

    ephemeridesCountFeedbackMessage() {
      return (
        'Expecting around ' +
        this.ephemeridesCount +
        ' ephemerides data with ' +
        this.ephemeridesData +
        '% of maximum ephemerides data'
      )
    },

    ephemeridesWarningMessage() {
      if (
        this.ephemeridesCount > 0 &&
        this.inputs.ephemeridesMeanChoice &&
        this.inputs.ephemeridesStepInSeconds < 86400
      ) {
        return 'Mean ephemerides can be slow to be computed, the generation of results will take a long time if the time step is too small. You should consider a time step bigger than one day.'
      }
      return ''
    },

    ephemeridesMaxNumberOfFieldsPerEphemeris() {
      return Object.values(this.ephemeridesTypesCount).reduce(
        (acc, curr) => acc + curr,
        0
      )
    },

    ephemeridesNumberOfFieldsPerEphemeris() {
      if (!this.inputs.ephemeridesRequested) {
        return 0
      }
      return this.inputs.ephemeridesRequested.reduce(
        (acc, curr) => acc + this.ephemeridesTypesCount[curr],
        0
      )
    },

    systemDefinedInSpacecraft() {
      return (
        this.inputs.spacecraft != null &&
        this.inputs.spacecraft.powerSystemDefined &&
        this.inputs.spacecraft.spacecraftGeometry &&
        this.inputs.spacecraft.spacecraftGeometry.type !== 'SPHERE'
      )
    },

    operationSystemDefinedInSpacecraft() {
      return this.inputs?.spacecraft?.operationSystemDefined
    },

    isNumericalPropagation() {
      return this.inputs.propagationType === 'NUMERICAL'
    },

    perturbationWatcher() {
      return this.inputs.perturbations
    },

    deltaOrbitalElementsReadOnly() {
      return this.inputs.targetDefinition === 'TARGET_ORBIT'
    },

    targetOrbitWatcher() {
      return this.inputs.targetOrbit
    },

    initialOrbitWatcher() {
      return this.inputs.initialOrbit
    },

    targetDefinitionWatcher() {
      return this.inputs.targetDefinition
    },

    orbitalDutyCycleInitializerWatcher() {
      if (
        this.inputs.orbitalDutyCycle === '' ||
        this.inputs.orbitalDutyCycle == null ||
        this.inputs.orbitalDutyCycle === 0
      ) {
        return this.maxMeanDutyCycle
      }
      return 'done'
    }
  },

  mounted() {
    this.refreshSpacecrafts()
    this.refreshOrbits()
    this.refreshForm()
    this.$root.$on('copy-mission', (mission) => {
      this.copyMission(mission)
    })
    this.$root.$on('create-mission', (mission) => {
      this.createMission(mission)
    })
    this.$root.$on('current-mission-updated', () => {
      this.refreshForm()
    })
  },

  watch: {},

  data() {
    return {
      tableFields: [
        { key: 'displayedName', label: 'Name', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'spacecraft', label: 'Spacecraft', sortable: true },
        { key: 'payload', label: 'Payload', sortable: true },
        { key: 'antenna', label: 'Antenna', sortable: true },
        { key: 'initialOrbit', label: 'Initial orbit', sortable: true },
        {
          key: 'simulationDuration',
          label:
            'Mission duration' +
            this.getResultDefaultUnitLabel('simulationDuration'),
          sortable: true
        },
        {
          key: 'totalDeltaV',
          label: 'ΔV' + this.getResultDefaultUnitLabel('totalDeltaV'),
          sortable: true
        },
        {
          key: 'totalConsumption',
          label:
            'Used propellant' +
            this.getResultDefaultUnitLabel('totalConsumption')
        },
        { key: 'results', label: 'Result' },
        { key: 'actions', label: 'Actions' }
      ],
      ephemeridesTypesCount: {
        CARTESIAN: 6,
        CIRCULAR: 8,
        EQUINOCTIAL: 8,
        KEPLERIAN: 8,
        SYSTEM: 1,
        ATTITUDE: 3,
        ECLIPSE: 1,
        THRUST: 5,
        MANOEUVRING_STRATEGIES: 1
      },

      orbitsNotParametrics: [],
      spacecraftsNotParametrics: [],
      enumPropertiesList: this.getEnumProperties(),

      missionPlot: null,

      modalRef: { content: [] },
      showModal: false,
      maneuveringStrategies: {},

      errorThrustDuration: '',

      inputs: {
        initialOrbit: null,
        targetOrbit: null,
        measureGenerationRequest: null,
        type: this.getDefaultValue('type'),
        targetDefinition: this.getDefaultValue('targetDefinition'),
        optimizationType: this.getDefaultValue('optimizationType'),
        raanPhasingReference: this.getDefaultValue('raanPhasingReference'),
        orbitPhasingOptimizationType: this.getDefaultValue(
          'orbitPhasingOptimizationType'
        ),
        spacecraft: null,
        manoeuvreRoadmap: null,
        missionDateTime: this.getDefaultValue('missionDateTime'),
        maximumDuration: this.getDefaultValue('maximumDuration'),
        duration: this.getDefaultValue('duration'),
        propagationType: this.getDefaultValue('propagationType'),
        estimatorType: this.getDefaultValue('estimatorType'),
        deltaInclination: this.getDefaultValue('deltaInclination'),
        deltaEccentricity: this.getDefaultValue('deltaEccentricity'),
        deltaSemiMajorAxis: this.getDefaultValue('deltaSemiMajorAxis'),
        perturbations: this.getDefaultValue('perturbations'),
        customSolarFlux: this.getDefaultValue('customSolarFlux'),
        earthPotentialConfiguration: this.getDefaultValue(
          'earthPotentialConfiguration'
        ),
        customEarthPotentialOrder: this.getDefaultValue(
          'customEarthPotentialOrder'
        ),
        customEarthPotentialDegree: this.getDefaultValue(
          'customEarthPotentialDegree'
        ),
        coastingDomain: this.getDefaultValue('coastingDomain'),
        minCoastingAltitude: this.getDefaultValue('minCoastingAltitude'),
        maxCoastingAltitude: this.getDefaultValue('maxCoastingAltitude'),
        minCoastingInclination: this.getDefaultValue('minCoastingInclination'),
        maxCoastingInclination: this.getDefaultValue('maxCoastingInclination'),
        targetMissionDuration: this.getDefaultValue('targetMissionDuration'),
        targetMissionDurationForAol: this.getDefaultValue(
          'targetMissionDurationForAol'
        ),
        targetDeltaRaan: this.getDefaultValue('targetDeltaRaan'),
        targetDeltaLtan: this.getDefaultValue('targetDeltaLtan'),
        targetRaanLtanType: this.getDefaultValue('targetRaanLtanType'),
        targetDeltaVmax: this.getDefaultValue('targetDeltaVmax'),
        targetDeltaArgumentOfLatitude: this.getDefaultValue(
          'targetDeltaArgumentOfLatitude'
        ),
        thrustArcsNumber: this.getDefaultValue('thrustArcsNumber'),
        thrustArcDuration: this.getDefaultValue('thrustArcDuration'),
        maneuveringStrategyChoice: this.getDefaultValue(
          'maneuveringStrategyChoice'
        ),
        thrustArcsPosition: this.getDefaultValue('thrustArcsPosition'),
        numberOfThrustOrbits: this.getDefaultValue('numberOfThrustOrbits'),
        numberOfRestOrbits: this.getDefaultValue('numberOfRestOrbits'),
        numberOfShiftOrbits: this.getDefaultValue('numberOfShiftOrbits'),
        stopThrustInEclipse: this.getDefaultValue('stopThrustInEclipse'),
        dynamicDutyCycleSimulated: this.getDefaultValue(
          'dynamicDutyCycleSimulated'
        ),
        orbitalDutyCycle: this.getDefaultValue('orbitalDutyCycle'),
        reference: this.getDefaultValue('reference'),
        deltaMeanLongitudeArgument: this.getDefaultValue(
          'deltaMeanLongitudeArgument'
        ),
        thrustArcMeanLongitudeArgument: this.getDefaultValue(
          'thrustArcMeanLongitudeArgument'
        ),
        ephemeridesOscChoice: this.getDefaultValue('ephemeridesOscChoice'),
        ephemeridesMeanChoice: this.getDefaultValue('ephemeridesMeanChoice'),
        ephemeridesThreedChoice: this.getDefaultValue(
          'ephemeridesThreedChoice'
        ),
        ephemeridesRequested: this.getDefaultValue('ephemeridesRequested'),
        ephemeridesStepInSeconds: this.getDefaultValue(
          'ephemeridesStepInSeconds'
        ),
        overloadOrbitDateTime: this.getDefaultValue('overloadOrbitDateTime'),
        thrustAttitudeMode: this.getDefaultValue('thrustAttitudeMode'),
        restAttitudeMode: this.getDefaultValue('restAttitudeMode'),
        thrustCustomPointingAxisT: this.getDefaultValue(
          'thrustCustomPointingAxisT'
        ),
        thrustCustomPointingAxisN: this.getDefaultValue(
          'thrustCustomPointingAxisN'
        ),
        thrustCustomPointingAxisW: this.getDefaultValue(
          'thrustCustomPointingAxisW'
        ),
        restCustomPointingAxisT: this.getDefaultValue(
          'restCustomPointingAxisT'
        ),
        restCustomPointingAxisN: this.getDefaultValue(
          'restCustomPointingAxisN'
        ),
        restCustomPointingAxisW: this.getDefaultValue(
          'restCustomPointingAxisW'
        ),
        freeFlyingAttitudeMode: this.getDefaultValue('freeFlyingAttitudeMode'),
        layout: this.getDefaultValue('layout'), // to save the dashboard, should be optional in fieldsdescription to be automatically copied
        smaTolerance: this.getDefaultValue('smaTolerance'),
        Payload: null,
        targetDateDefinitionType: this.getDefaultValue(
          'targetDateDefinitionType'
        ),
        targetDate: this.getDefaultValue('targetDate')
      },

      modalInfo: { title: '', content: '', config: {} },
      modalError: { title: '', content: '' },
      displayed: null
    }
  },

  methods: {
    copyMissionHandler(mission) {
      this.copyMission(mission)
    },
    createMissionHandler(mission) {
      this.createMission(mission)
    },
    currentMissionUpdatedHandler() {
      this.refreshForm()
    },

    async getDefaultRoadmap() {
      let data = await this.$http
        .post(
          '/physics/buildRoadmapWithMultipleThrust',
          this.inputs.initialOrbit.orbitDateTime
        )
        .then(function (response) {
          return response.data
        })
        .catch((e, __) => {
          this.errorTrace = this.getErrorMessage(e)
          return null
        })
      this.inputs.manoeuvreRoadmap = data
      return data
    },

    setMeasureGenerationRequest(data) {
      //TODO EES clean set of measurement request
      this.inputs.measureGenerationRequest = { requestedMeasures: '' }
      this.inputs.measureGenerationRequest.requestedMeasures = data
    },

    getDateTimeString() {
      let date = new Date(this.inputs.initialOrbit.orbitDateTime)
      return date.toUTCString()
    },

    getCurrentFormTitle() {
      return this.inputs.type + ' Mission'
    },

    createMission(mission) {
      if (mission && mission.typeName === this.getCurrentFormType()) {
        this.clearForm()
        this.inputs.perturbations = [
          'EARTH_POTENTIAL',
          'SRP',
          'THIRD_BODY',
          'DRAG'
        ]
        this.inputs.type = mission.type
        // set default configuration
        this.sendFormToServer()
      }
    },

    copyMission(mission) {
      if (mission && mission.typeName === this.getCurrentFormType()) {
        this.copy(mission)
        this.sendFormToServer()
      }
    },

    clearLocalFields() {
      for (let field in this.getPropertiesDescription()) {
        if (this.inputs.hasOwnProperty(field)) {
          this.inputs[field] = this.getDefaultValue(field)
        }
      }
    },

    refreshForm() {
      if (
        this.currentMission != null &&
        this.currentMission.typeName === this.getCurrentFormType()
      ) {
        if (this.currentMission.id !== this.inputs.id) {
          // different mission reload completely the form
          this.edit(this.currentMission, 0)
        } else {
          // reloading the current mission
          // only refresh the updated fields
          this.updateAllReferences()
          this.inputs.layout = this.currentMission.layout // might be saved from dashboard
        }
      }
    },

    updateAllReferences() {
      this.inputs.editing = true
      this.checkReferences(this.currentMission, this.inputs)
    },

    checkReferences(missionSource, missionToUpdate) {
      if (missionSource != null && missionToUpdate != null) {
        for (let field in this.getPropertiesDescription()) {
          if (missionSource[field] != null && missionToUpdate[field] != null) {
            if (
              missionSource[field].id != null &&
              missionSource[field].id === missionToUpdate[field].id
            ) {
              missionToUpdate[field] = missionSource[field]
            }
          }
        }
      }
    },

    sendFormToServer() {
      this.inputs.saving = true
      this.$http
        .post(this.getCurrentAPI(), this.createObjectFromForm())
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('set-current-mission', response.data)
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },
    // redefine from form.js to keep the form open on the upadted data
    onSave(evt) {
      this.inputs.saving = true
      this.$http
        .put(
          this.getCurrentAPI() + '/' + this.inputs.id,
          this.createObjectFromForm()
        )
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('update-mission-list')
          this.$root.$emit('set-current-mission', response.data) // update fields and current mission
          // redirect to dashboard

          this.$router.push({
            path:
              '/' +
              missionTools.missionUrlMapping[response.data.typeName].dashboard
          })
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },
    getResultFieldType(field) {
      return dataTools.getFieldTypeFromPath(
        ['maneuverReport', field],
        context,
        null
      )
    },

    getResultDefaultUnitLabel(field) {
      let fieldType = this.getResultFieldType(field)
      return (
        ' ' + dataTools.getUnitLabel(dataTools.getDefaultGuiUnit(fieldType))
      )
    },

    // /////////////// TODO should be merged with mission ///////////
    convertToArray(elements) {
      if (!Array.isArray(elements)) {
        return [elements]
      } else {
        return elements
      }
    },

    removeParameter(index) {
      this.configuration.targets.splice(index, 1)
    },

    mergeString(inputElements, fieldPath) {
      let uniqueVals = dataTools.getUniqueValues(
        this.convertToArray(inputElements),
        fieldPath
      )
      if (uniqueVals.length === 1) {
        return uniqueVals[0]
      } else {
        return uniqueVals
      }
    },

    isStringInside(expected, inputElements, fieldPath) {
      let elements = this.convertToArray(inputElements)
      return (
        elements.filter(
          (element) => dataTools.getValue(element, fieldPath) === expected
        ).length > 0
      ) // TODO MFI stop on first would be better
    },

    // Methods to manage result popup
    info(mission, button) {
      this.modalInfo.title = mission.name
      this.modalInfo.content = [mission]
      this.modalInfo.errorLog = mission.computationLog
      this.$root.$emit('bv::show::modal', 'modalInfo', button)
    },

    resetModal() {
      this.modalInfo.title = ''
      this.modalInfo.content = ''
      this.modalError.title = ''
      this.modalError.content = ''
      this.modalInfo.errorLog = ''
    },

    // TODO MFI delete ?
    displayErrorLog(mission, button) {
      this.modalError.title = mission.name
      this.modalError.content = mission.computationLog
      this.$root.$emit('bv::show::modal', 'modalError', button)
    },

    // Methods to compute &  export result
    displayPlot(mission) {
      if (!mission) {
        this.$refs.plotMission.setMission(null, null, null)
        return
      }
      if (this.displayed === null || this.displayed === '3d') {
        this.$refs.three.setMission(null, null, null)
        this.displayed = 'plot'
      }
      this.missionPlot = mission
      this.$refs.plotMission.setMission(
        mission,
        mission.results.ephemerides,
        mission.results.fieldIndexes
      )
    },

    setMissionConfiguration(config) {
      this.missionPlot.plotConfiguration = JSON.parse(JSON.stringify(config)) // copy values, not ref
    },

    addLocalFieldsFromForm(baseObject, fieldsList) {
      this.addFieldsListToBaseObject(baseObject, fieldsList)
      baseObject.computationRequested = true // reset computation
      return baseObject
    },

    // methods to mange form & table like other components

    getCurrentFormType() {
      return 'SimulationRequest'
    },

    refreshSpacecrafts() {
      let self = this
      this.$http
        .get(this.getAPI('spacecraftdefinitions'))
        .then((response) => {
          self.spacecraftsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of spacecrafts. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },

    refreshOrbits() {
      let self = this
      this.$http
        .get(this.getAPI('orbitdefinitions'))
        .then((response) => {
          self.orbitsNotParametrics = response.data
            .filter((obj) => !obj.displayedName.endsWith('*'))
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of orbits. ',
            e,
            'NumericalMissionErrorDisplay',
            'error'
          )
        })
    },

    getRefList(param) {
      if (param === 'initialOrbit' || param === 'targetOrbit') {
        return this.orbits
      } else if (param === 'spacecraft') {
        return this.spacecrafts
      }
    },

    getMissionType(mission) {
      let types = { type: mission.type }
      if (mission.type === 'RAAN Phasing') {
        types.optimizationType = mission.optimizationType
        types.targetRaanLtanType = mission.targetRaanLtanType
        types.coastingDomain = mission.coastingDomain
        types.targetDefinition = mission.targetDefinition
        types.maneuveringStrategyChoice = mission.maneuveringStrategyChoice
        if (types.maneuveringStrategyChoice === 'CUSTOM') {
          types.thrustArcsPosition = mission.thrustArcsPosition
        }
      }
      if (mission.type === 'Orbit Phasing') {
        types.orbitPhasingOptimizationType =
          mission.orbitPhasingOptimizationType
        types.maneuveringStrategyChoice = mission.maneuveringStrategyChoice
        if (types.maneuveringStrategyChoice === 'CUSTOM') {
          types.thrustArcsPosition = mission.thrustArcsPosition
        }
      }
      if (mission.type === 'Orbital Transfer') {
        types.targetDefinition = mission.targetDefinition
        types.maneuveringStrategyChoice = mission.maneuveringStrategyChoice
        if (types.maneuveringStrategyChoice === 'CUSTOM') {
          types.thrustArcsPosition = mission.thrustArcsPosition
        }
      }
      if (mission.type === 'LEO Station Keeping') {
        types.targetDefinition = mission.targetDefinition
        types.maneuveringStrategyChoice = mission.maneuveringStrategyChoice
        if (types.maneuveringStrategyChoice === 'CUSTOM') {
          types.thrustArcsPosition = mission.thrustArcsPosition
        }
      }
      if (mission.type === 'Orbit Extrapolation') {
        types.targetDateDefinitionType = mission.targetDateDefinitionType
      }
      if (mission.type === 'Maneuvering Strategy Design') {
        types.restAttitudeMode = mission.restAttitudeMode
        types.thrustAttitudeMode = mission.thrustAttitudeMode
      }
      types.earthPotentialConfiguration = mission.earthPotentialConfiguration
      return types
    },

    getRequiredPropertiesList(instance) {
      let missionType = this.getMissionType(instance)
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        missionType,
        'required'
      )
    },

    getOptionalPropertiesList(instance) {
      let missionType = this.getMissionType(instance)
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        missionType,
        'optional'
      )
    },

    getAllPropertiesUnionList() {
      let objectType = this.getCurrentFormType()
      let requiredList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'required'
      )
      let optionalList = dataTools.getPropertiesList(
        fieldsDescription[objectType],
        null,
        'optional'
      )
      return [...new Set(requiredList.concat(optionalList))]
    },

    // ---------------------- field checks -----------------
    // duplicated for special check on perturbations
    getEnumFieldState(parentObject, fieldName, required) {
      let state = {
        value: true, // do not initialize with null, it means field empty
        message: true
      }
      if (parentObject[fieldName] == null && !required) {
        return state
      }
      let fieldDesc = this.getPropertiesDescription()[fieldName]
      state.value = false
      state.message = 'Please select a valid value'
      if (Array.isArray(parentObject[fieldName])) {
        if (required && parentObject[fieldName].length === 0) {
          state.value = false
          state.message = 'Please select at least one value'
        } else {
          // if multi select (no value set can be a right value)
          state.value = true
          state.message = true
        }
      } else {
        for (let value in fieldDesc.values) {
          if (fieldDesc.values[value].value === parentObject[fieldName]) {
            state.value = true
            state.message = true
          }
        }
      }
      if (fieldName === 'perturbations' && parentObject.spacecraft != null) {
        if (
          parentObject[fieldName] &&
          parentObject[fieldName].includes('SRP') &&
          !parentObject.spacecraft.srpModelDefined
        ) {
          state.value = false
          state.message =
            'Sun radiation pressure model not defined in spacecraft'
        }
        if (
          parentObject[fieldName] &&
          parentObject[fieldName].includes('DRAG') &&
          !parentObject.spacecraft.dragModelDefined
        ) {
          state.value = false
          state.message = 'Drag model not defined in spacecraft'
        }
      }
      return state
    }
  }
}
</script>
